import { Link } from "react-router-dom";
import { ShieldCheck, Target, Globe, Heart, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";

const VALUES = [
  { icon: Heart, title: "People-Centered", desc: "People are at the center of every successful business and every decision we guide." },
  { icon: ShieldCheck, title: "Confidentiality", desc: "Professionalism and discretion in every engagement, always." },
  { icon: Target, title: "Practical Expertise", desc: "Real, applicable guidance grounded in operational experience." },
  { icon: Globe, title: "Thoughtful Service", desc: "Boutique attention that treats your goals as our own." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO title="About" description="Founded by an MBA-educated, PHR-certified HR professional, people, redefined delivers thoughtful, practical, people-first HR and career services." />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-20 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">Founder-led. People-obsessed.</span>
            <h1 className="font-serif text-5xl md:text-7xl text-espresso mt-5 leading-[1.02]">
              A boutique practice built on people.
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 space-y-5 text-lg text-espresso/75 leading-relaxed">
              <p>
                People, redefined is founded by an MBA-educated, PHR-certified HR professional with
                experience in recruiting, HR operations, employee support, payroll, benefits, and
                organizational processes across fast-paced industries — including professional sports
                and hospitality.
              </p>
              <p>
                That background shaped a simple belief: people are at the center of every successful
                business. We bring thoughtful service, practical expertise, professionalism, and
                confidentiality to every engagement — for companies building better workplaces and
                professionals elevating their careers.
              </p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="border-l border-champagne/50 pl-8 py-6">
              <span className="font-serif italic leading-[1.05] text-espresso block" style={{ fontSize: "clamp(1.75rem,4vw,3rem)" }}>people, redefined.</span>
              <span className="mt-4 block text-xs tracking-[0.35em] uppercase text-espresso/60">people-first, always</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-espresso/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-10 lg:gap-16">
          <Reveal>
            <div className="border-t border-espresso/15 pt-8 h-full">
              <h2 className="font-serif text-3xl text-espresso">Our Mission</h2>
              <p className="text-espresso/75 mt-4 leading-relaxed">
                To help businesses build better workplaces and professionals move forward with
                confidence — through personalized, flexible, people-first solutions.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border-t border-espresso/15 pt-8 h-full">
              <h2 className="font-serif text-3xl text-espresso">Our Vision</h2>
              <p className="text-espresso/75 mt-4 leading-relaxed">
                A world where HR feels human and career growth feels possible — where intentional,
                boutique support is accessible to the businesses and people who need it most.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
        <Reveal><h2 className="font-serif text-4xl md:text-5xl text-espresso">Brand values.</h2></Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12 mt-12">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="border-t border-espresso/15 pt-6 h-full">
                <v.icon className="text-champagne" size={28} />
                <h3 className="font-serif text-xl text-espresso mt-5">{v.title}</h3>
                <p className="text-espresso/70 mt-2 text-sm leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-espresso/10">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-serif text-4xl md:text-6xl text-espresso leading-[1.02]">Let's build something meaningful.</h2>
          <Link to="/book" data-testid="about-cta-book" className="inline-flex items-center gap-2 mt-8 bg-espresso text-ivory rounded-full px-8 py-4 font-medium hover:bg-forest transition-colors">
            Book a Consultation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
